import React, { useState, useMemo, useCallback } from 'react';
import { getMessage } from '@tcc/i18n';
import { Icon, Link } from '@tcc/crate';
import cx from 'classnames';
import { Nullable } from '@tcc/common';
import { ApplicationsData, ApplicationProduct, Application } from '../../../types/Application';
import { onClickApplicationReport } from '../../../utils/fieldFeatures';
import { sortApplicationProducts } from '../../../utils/applicationSorting';
import { SortField, SortDirection } from '../../../types/Sort';
import ApplicationHeader from './ApplicationHeader';
import ApplicationMetrics from './ApplicationMetrics';
import ProductTable from './ProductTable';
import styles from './ApplicationsPanel.css';
import { LONG_FORMAT } from '../../../utils/dateHelpers';

export interface ApplicationsDetailsProps {
  applicationsData: ApplicationsData;
  testId?: string;
  expandedApplication: Nullable<string>;
  onToggleApplicationExpand: (applicationKey: Nullable<string>, application: Application) => void;
  season?: string;
  locale: string;
}


const ApplicationsDetails = ({
  applicationsData,
  testId = 'application-details',
  expandedApplication = null,
  onToggleApplicationExpand,
  season,
  locale
}: ApplicationsDetailsProps) => {
  const { applications } = applicationsData;
  const applicationCount = applications.length;
  
  const [sortField, setSortField] = useState<SortField>(SortField.NAME);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);

  const formatDate = useCallback((date: Date): string => date.toLocaleDateString(locale, LONG_FORMAT), [locale]);

  const sortProducts = useCallback((products: ApplicationProduct[]): ApplicationProduct[] => 
    sortApplicationProducts(products, sortField, sortDirection), [sortField, sortDirection]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASC);
    }
  }, [sortField, sortDirection]);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortField !== field) return Icon.set.ARROW_UP;
    return sortDirection === SortDirection.ASC ? Icon.set.ARROW_UP : Icon.set.ARROW_DOWN;
  }, [sortField, sortDirection]);

  const sortedApplications = useMemo(() => 
    applications
      .slice()
      .sort((a, b) => b.date.getTime() - a.date.getTime()),
    [applications]
  );

  const title = `${getMessage('application.card.title')} (${applicationCount})`;

  const handleReportClick = useCallback(() => {
    if (!season) {
      return;
    }
    const redirectToReport = onClickApplicationReport(season);
    redirectToReport();
  }, [season]);
  return (
    <div className={styles.applicationsPanelContainer} data-test-id={testId} data-testid={testId}>
      <div className={styles.applicationsPanelTitleRow}>
        <h2 className={styles.applicationsPanelTitle}>{title}</h2>
      </div>

      <div className={styles.applicationsPanelList}>
        {sortedApplications.map((application, index) => {
          const applicationKey = `${application.date.getTime()}_${encodeURIComponent(application.name)}`;
          const isApplicationExpanded = expandedApplication === applicationKey;
          const testId = application.name === 'Headline' 
            ? `application-card-${application.name}`
            : `application-card-${index}`;

          return (
            <div 
              key={applicationKey}
              className={cx(
                styles.applicationsPanelItem,
                { [styles.applicationsPanelItemExpanded]: isApplicationExpanded }
              )}
              data-test-id={testId}
            >
              <ApplicationHeader
                name={application.name}
                date={application.date}
                thumbnailUri={application.thumbnailUri}
                isExpanded={isApplicationExpanded}
                onToggle={() => onToggleApplicationExpand(applicationKey, application)}
                testId={testId}
                formatDate={formatDate}
              />

              {isApplicationExpanded && application.details && (
                <div className={styles.applicationsPanelDetails} data-test-id={`${testId}-metrics`}>
                  <ApplicationMetrics
                    area={application.details.area}
                    applicationRate={application.details.applicationRate}
                    totalAmountApplied={application.details.totalAmountApplied}
                    machineSpeed={application.details.machineSpeed}
                  />
                  
                  <ProductTable
                    products={application.details.products}
                    sortField={sortField}
                    sortProducts={sortProducts}
                    handleSort={handleSort}
                    getSortIcon={getSortIcon}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        className={styles.applicationsPanelReportLink}
        onClick={handleReportClick}
        testId={`${testId}-report-link`}
      >
        {getMessage('application.report.button')}
      </Link>
    </div>
  );
};

export default ApplicationsDetails;
